import moment from 'moment';

import { SchoolHolidayTerm, Session } from '../../legacy/types';
import { REVISION_PERIOD_DAYS, WEEK_BEFORE_TERM_START_DAYS } from './form-steps/PauseEnrolmentForm/constants';

export const calculatePauseStartDate = (
  pauseMethod: string | null,
  specificPauseStartDate: string | null,
  currentOrUpcomingHoliday: SchoolHolidayTerm
): string | null => {
  if (pauseMethod === 'pause-by-weeks') {
    // When skipping by weeks, sessions within 48 hours are not affected; pause starts after 3 days (existing behavior)
    return moment().startOf('day').add(3, 'days').toISOString();
  } else if (pauseMethod === 'pause-during-holidays' && !!currentOrUpcomingHoliday) {
    // Return the later of: current time or holiday start date
    // This ensures we don't set a pause start date in the past if the holiday has already started
    const holidayStartDate = moment(currentOrUpcomingHoliday.startDate);
    const now = moment();
    return moment.max(now, holidayStartDate).toISOString();
  } else if (pauseMethod === 'pause-by-specific-date' && !!specificPauseStartDate) {
    return moment(specificPauseStartDate).toISOString();
  }
  return null;
};

export const calculatePauseEndDate = (
  pauseMethod: string | null,
  pauseStartDate: string | null,
  pauseByWeeksValue: string | null,
  currentOrUpcomingHoliday: SchoolHolidayTerm
): string | null => {
  if (pauseMethod === 'pause-during-holidays' && !!currentOrUpcomingHoliday) {
    return moment(currentOrUpcomingHoliday.endDate).toISOString();
  }
  if (!pauseStartDate || !pauseByWeeksValue || !pauseMethod) {
    // early return to avoid calculating
    return null;
  }
  if (pauseMethod === 'pause-by-weeks') {
    const weeksNumber = Number(pauseByWeeksValue);
    if (isNaN(weeksNumber) || weeksNumber <= 0) {
      return null;
    }
    const calculatedDate = moment(pauseStartDate).add(weeksNumber, 'weeks');
    if (calculatedDate.isValid()) {
      return calculatedDate.toISOString();
    }
  }
  return null;
};

export const calculateResumptionDate = (
  pauseResumption: string,
  nextSchoolTerm: SchoolHolidayTerm,
  specificResumptionDate: string,
  pauseMethod: string,
  pauseEndDate: string
) => {
  if (pauseMethod === 'pause-by-weeks' || pauseMethod === 'pause-during-holidays') {
    return moment(pauseEndDate).add(1, 'day').toISOString();
  }
  if (pauseResumption === 'resume-on-start-of-term') {
    // next term startDate
    return moment(nextSchoolTerm.startDate).toISOString();
  } else if (pauseResumption === 'resume-a-week-before-term-start') {
    return moment(nextSchoolTerm.startDate).subtract(WEEK_BEFORE_TERM_START_DAYS, 'days').toISOString();
  } else if (pauseResumption === 'resume-on-revision-period') {
    return moment(nextSchoolTerm.startDate).subtract(REVISION_PERIOD_DAYS, 'days').toISOString();
  } else if (pauseResumption === 'resume-on-specific-date' && !!specificResumptionDate) {
    return moment(specificResumptionDate).toISOString();
  }
  return null;
};

export const calculateAffectedSessions = (
  enrolmentId: string,
  sessions: Session[],
  pauseStartDate: string | null,
  pauseEndDate: string | null,
  getAffectedSessions: (params: { enrolmentId: string; sessions: Session[]; start: string; end: string }) => Session[]
): Session[] => {
  if (!pauseStartDate || !pauseEndDate) {
    return [];
  }

  return getAffectedSessions({
    enrolmentId,
    sessions,
    start: moment(pauseStartDate).toISOString(),
    end: moment(pauseEndDate).toISOString(),
  });
};

export const shouldShowPauseDuringHolidaysOption = (currentSchoolTerm: SchoolHolidayTerm | null): boolean => {
  return !!currentSchoolTerm;
};
